import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from 'react-daisyui';
import PaymentModal from 'components/Billing/PaymentModal';
import List from 'components/Billing/List'
import { connect } from 'react-redux';
import { BillingActions } from 'store/actions';
import { useSearchParams } from 'react-router-dom';
import { STATUS_FILTERS, TYPE_OPTIONS, getBillingParams } from './helper';
import toast from 'react-hot-toast';
import RadioFilter from 'components/RadioFilter';
import DropdownFilter from 'components/DropdownFilter';

let enhancer = connect(null, { ...BillingActions });

let initialParams = {
  page: 1,
  items: 10
}

const Billing = ({ getBilling }) => {
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [billingData, setBillingData] = useState(null);
  const [params, setParams] = useState(initialParams);
  const [pageEnd, setPageEnd] = useState(false);
  const [outstandingSummary, setOutstandingSummary] = useState(null);

  const { unpaid_treatment_plan_count, unpaid_charge_count, balance_due } = outstandingSummary || {}

  const statusFilter = searchParams.get('status') || 'all';
  const typesParam = searchParams.get('types');
  const selectedTypes = typesParam ? typesParam.split(',') : ['treatment_plan', 'charge'];

  const setStatusFilter = (value: string) => {
    setBillingData(null)
    setSearchParams(prev => {
      if (value == 'all') prev.delete('status');
      else prev.set('status', value);
      return prev;
    }, { replace: true });
  };

  const setSelectedTypes = (valueOrUpdater: string[] | ((prev: string[]) => string[])) => {
    setBillingData(null)
    const updatedTypes = typeof valueOrUpdater === 'function' ? valueOrUpdater(selectedTypes) : valueOrUpdater;
    setSearchParams(prev => {
      if (updatedTypes.length === 0 || updatedTypes.length === TYPE_OPTIONS.length) {
        prev.delete('types');
      } else {
        prev.set('types', updatedTypes.join(','));
      }
      return prev;
    }, { replace: true });
  };

  useEffect(() => {
    loadBillings()
  }, [statusFilter, typesParam]);

  const loadBillings = async (_params = initialParams) => {
    try {
      const { data, pagy, meta } = await getBilling({ ..._params, ...getBillingParams(statusFilter, selectedTypes) });

      setBillingData(prev => _params.page == 1 ? data : [...(prev ?? []), ...data])
      setOutstandingSummary(meta)
      setParams(_params)
      setPageEnd(!pagy?.next)

    } catch (error) {
      toast.error(error.errors || 'Failed to load billing data');
      setBillingData([]);
    }
  };

  const loadMore = () => {
    loadBillings({ ...params, page: params.page + 1 });
  }

  const handleClosePaymentModal = useCallback(() => {
    setShowAddPayment(false);
  }, []);

  return (
    <>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-secondary-900">Billing</h1>
            <p className="text-sm text-secondary-500 mt-0.5">Your balance and payment activity.</p>
          </div>
          <Button
            size="sm"
            className="bg-secondary-900 text-white rounded-xl border-none gap-1.5 hover:bg-secondary-800"
            onClick={() => setShowAddPayment(true)}
          >
            <Plus size={15} />
            Add Payment
          </Button>
        </div>

        {!balance_due ?
          <div className="bg-gradient-to-br from-secondary-900 to-slate-700 rounded-2xl p-5 mb-5 text-white">
            <div className="skeleton bg-secondary-700 h-3 w-28 rounded-lg mb-3"></div>
            <div className="skeleton bg-secondary-700 h-8 w-40 rounded-lg"></div>
            <div className="skeleton bg-secondary-700 h-3 w-60 rounded-lg mt-3"></div>
          </div>
          :
          <div className="bg-gradient-to-br from-secondary-900 to-slate-700 rounded-2xl p-5 mb-5 text-white">
            <p className="text-xs opacity-60 mb-2 uppercase tracking-wide font-medium">Balance Due</p>
            <p className="text-4xl font-bold tracking-tight">
              ${balance_due.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs opacity-50 mt-2">
              {unpaid_treatment_plan_count} treatment plan{unpaid_treatment_plan_count > 1 ? 's' : ''} · {unpaid_charge_count} unpaid charge{unpaid_charge_count > 1 ? 's' : ''}
            </p>
          </div>
        }

        <div className="flex items-center gap-2">
          <RadioFilter
            options={STATUS_FILTERS}
            value={statusFilter}
            onChange={setStatusFilter}
          />
          <DropdownFilter
            options={TYPE_OPTIONS}
            value={selectedTypes}
            onChange={setSelectedTypes}
          />
        </div>

        <List
          billingData={billingData}
          loadMore={loadMore}
          pageEnd={pageEnd}
        />
      </div>

      <PaymentModal
        open={showAddPayment}
        onClose={handleClosePaymentModal}
      />
    </>
  );
};

export default enhancer(Billing);
